import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Unlock, MapPin, Calendar, DollarSign, Phone, Mail, User, Clock, CheckCircle, Droplet, Zap, Thermometer, Wrench, Sparkles, CreditCard, Loader2, Crown, Star, Check, Share2, Car, MessageCircle, Home, Bug, Hammer, Trees, Truck, DoorOpen, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
  roofing: Home,
  "pest-control": Bug,
  remodeling: Hammer,
  landscaping: Trees,
  moving: Truck,
  "garage-door": DoorOpen,
  "appliance-repair": Settings,
};

const serviceColors = {
  plumbing: "bg-blue-100 text-blue-700",
  electrical: "bg-yellow-100 text-yellow-700",
  hvac: "bg-cyan-100 text-cyan-700",
  handyman: "bg-orange-100 text-orange-700",
  cleaning: "bg-purple-100 text-purple-700",
  mechanics: "bg-red-100 text-red-700",
  roofing: "bg-slate-100 text-slate-700",
  "pest-control": "bg-green-100 text-green-700",
  remodeling: "bg-amber-100 text-amber-700",
  landscaping: "bg-emerald-100 text-emerald-700",
  moving: "bg-indigo-100 text-indigo-700",
  "garage-door": "bg-gray-100 text-gray-700",
  "appliance-repair": "bg-teal-100 text-teal-700",
};

const LeadsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workerEmail, setWorkerEmail] = useState("");
  const [workerId, setWorkerId] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [purchasingLeadId, setPurchasingLeadId] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedLeadForPurchase, setSelectedLeadForPurchase] = useState(null);
  const [processingPlan, setProcessingPlan] = useState(null);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");
    
    if (sessionId && success === "true") {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please refresh the page.");
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your access has been updated.");
        setCheckingPayment(false);
        navigate("/leads", { replace: true });
        if (workerId) {
          fetchLeads(workerId);
        }
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingPayment(false);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const fetchLeads = async (wId) => {
    try {
      const [leadsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/leads/${wId}`),
        axios.get(`${API}/pricing`)
      ]);
      setLeads(leadsRes.data.leads);
      setSubscription(leadsRes.data.subscription);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/workers`);
      const worker = response.data.find(w => w.email.toLowerCase() === workerEmail.toLowerCase());
      
      if (worker) {
        setWorkerId(worker.id);
        localStorage.setItem("workerId", worker.id);
        localStorage.setItem("workerEmail", workerEmail);
        fetchLeads(worker.id);
      } else {
        toast.error("No worker found with this email. Please register first.");
        setLoading(false);
      }
    } catch (error) {
      toast.error("Failed to login. Please try again.");
      setLoading(false);
    }
  };

  const handleUnlockLead = async (leadId) => {
    // Check if has credits
    if (subscription?.has_unlimited || subscription?.leads_remaining > 0) {
      // Use existing credits
      setPurchasingLeadId(leadId);
      try {
        const response = await axios.post(`${API}/leads/${workerId}/unlock/${leadId}`);
        toast.success("Lead unlocked!");
        fetchLeads(workerId);
      } catch (error) {
        if (error.response?.status === 402) {
          // No credits, show pricing
          setSelectedLeadForPurchase(leadId);
          setShowPricingModal(true);
        } else {
          toast.error(error.response?.data?.detail || "Failed to unlock lead");
        }
      } finally {
        setPurchasingLeadId(null);
      }
    } else {
      // Show pricing modal
      setSelectedLeadForPurchase(leadId);
      setShowPricingModal(true);
    }
  };

  const handlePurchasePlan = async (planId) => {
    setProcessingPlan(planId);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/checkout`, {
        worker_id: workerId,
        plan_id: planId,
        lead_id: planId === "per_lead" ? selectedLeadForPurchase : null,
        origin_url: originUrl
      });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setProcessingPlan(null);
    }
  };

  const logContact = async (leadId, contactType) => {
    try {
      await axios.post(`${API}/contact-log?worker_id=${workerId}&lead_id=${leadId}&contact_type=${contactType}`);
    } catch (error) {
      console.log("Failed to log contact");
    }
  };

  // Check saved login
  useEffect(() => {
    const savedWorkerId = localStorage.getItem("workerId");
    const savedEmail = localStorage.getItem("workerEmail");
    
    if (savedWorkerId && savedEmail) {
      setWorkerId(savedWorkerId);
      setWorkerEmail(savedEmail);
      fetchLeads(savedWorkerId);
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("workerId");
    localStorage.removeItem("workerEmail");
    setWorkerId(null);
    setWorkerEmail("");
    setLeads([]);
    setSubscription(null);
  };

  // Login screen
  if (!workerId) {
    return (
      <div className="min-h-screen bg-white" data-testid="leads-login-page">
        <Navbar />
        <div className="container-custom py-20">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#e8f0fe] flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#1a73e8]" />
              </div>
              <h1 className="text-2xl font-medium text-[#202124] mb-2">
                Access Your Leads
              </h1>
              <p className="text-[#5f6368]">
                Enter your registered email to view and purchase leads
              </p>
            </div>

            <form onSubmit={handleLogin} className="form-container" data-testid="worker-login-form">
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#202124] mb-2 block">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Your registered email
                </Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={workerEmail}
                  onChange={(e) => setWorkerEmail(e.target.value)}
                  className="h-12 rounded-xl border-[#dadce0]"
                  required
                  data-testid="worker-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full py-3 h-auto"
                data-testid="worker-login-btn"
              >
                {loading ? "Loading..." : "View My Leads"}
              </Button>

              <p className="text-sm text-[#5f6368] text-center mt-4">
                Not registered yet?{" "}
                <button type="button" onClick={() => navigate("/join")} className="text-[#1a73e8] hover:underline">
                  Join as a Pro
                </button>
              </p>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Payment checking
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-white" data-testid="payment-checking-page">
        <Navbar />
        <div className="container-custom py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-[#e8f0fe] flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-[#1a73e8] animate-spin" />
            </div>
            <h2 className="text-2xl font-medium text-[#202124] mb-2">
              Verifying Payment...
            </h2>
            <p className="text-[#5f6368]">Please wait while we confirm your payment.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="leads-page">
      <Navbar />

      {/* Header */}
      <section className="py-8 bg-[#f8f9fa] border-b border-[#dadce0]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-medium text-[#202124]">Available Leads</h1>
              <p className="text-[#5f6368] text-sm">Logged in as: {workerEmail}</p>
            </div>
            
            {/* Subscription Status */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Referral Code */}
              {subscription?.referral_code && (
                <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-[#f8f9fa] rounded-full text-sm">
                  <Share2 className="w-4 h-4 text-[#5f6368]" />
                  <span className="text-[#5f6368]">Your code:</span>
                  <code className="font-mono font-medium text-[#1a73e8]">{subscription.referral_code}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/join?ref=${subscription.referral_code}`);
                      toast.success("Referral link copied!");
                    }}
                    className="text-[#1a73e8] hover:underline text-xs"
                  >
                    Copy
                  </button>
                </div>
              )}
              
              {subscription?.plan === "monthly_pro" && subscription?.is_active ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1a73e8] to-[#1557b0] text-white rounded-full">
                  <Crown className="w-4 h-4" />
                  <span className="font-medium">Pro Plan</span>
                  <span className="text-white/80">• Unlimited Leads</span>
                </div>
              ) : subscription?.plan === "monthly_basic" && subscription?.is_active ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-[#e8f0fe] text-[#1a73e8] rounded-full">
                  <Star className="w-4 h-4" />
                  <span className="font-medium">Basic Plan</span>
                  <span>• {subscription.leads_remaining} leads left</span>
                </div>
              ) : (
                <Button
                  onClick={() => {
                    setSelectedLeadForPurchase(null);
                    setShowPricingModal(true);
                  }}
                  className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
                  data-testid="upgrade-plan-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Get a Plan
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={handleLogout}
                className="rounded-full border-[#dadce0]"
                data-testid="logout-btn"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Leads Grid */}
      <section className="section-spacing">
        <div className="container-custom">
          {loading ? (
            <div className="grid-workers">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="worker-card animate-pulse p-6">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-20" data-testid="no-leads-message">
              <div className="w-16 h-16 rounded-full bg-[#f8f9fa] flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#5f6368]" />
              </div>
              <h3 className="text-xl font-medium text-[#202124] mb-2">No leads available yet</h3>
              <p className="text-[#5f6368]">Check back soon for new service requests in your area.</p>
            </div>
          ) : (
            <div className="grid-workers">
              {leads.map((lead) => {
                const Icon = serviceIcons[lead.service_category];
                return (
                  <div
                    key={lead.id}
                    className={`worker-card p-6 ${lead.is_unlocked ? "ring-2 ring-[#1e8e3e]" : ""}`}
                    data-testid={`lead-card-${lead.id}`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${serviceColors[lead.service_category]}`}>
                        {Icon && <Icon className="w-4 h-4" />}
                        {lead.service_category.charAt(0).toUpperCase() + lead.service_category.slice(1)}
                      </span>
                      {lead.is_unlocked ? (
                        <span className="badge-verified">
                          <Unlock className="w-3 h-3" />
                          Unlocked
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Locked
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-[#202124] mb-4 line-clamp-2">{lead.description}</p>

                    {/* Details */}
                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex items-center gap-2 text-[#5f6368]">
                        <MapPin className="w-4 h-4" />
                        {lead.location}
                      </div>
                      {lead.preferred_date && (
                        <div className="flex items-center gap-2 text-[#5f6368]">
                          <Calendar className="w-4 h-4" />
                          Preferred: {lead.preferred_date}
                        </div>
                      )}
                      {lead.budget && (
                        <div className="flex items-center gap-2 text-[#5f6368]">
                          <DollarSign className="w-4 h-4" />
                          Budget: {lead.budget}
                        </div>
                      )}
                    </div>

                    {/* Contact Info */}
                    <div className="border-t border-[#dadce0] pt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-[#5f6368]" />
                        <span className={lead.is_unlocked ? "text-[#202124]" : "text-gray-400"}>
                          {lead.customer_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-[#5f6368]" />
                        <span className={lead.is_unlocked ? "text-[#1a73e8]" : "text-gray-400"}>
                          {lead.customer_email}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-[#5f6368]" />
                        <span className={lead.is_unlocked ? "text-[#202124]" : "text-gray-400"}>
                          {lead.customer_phone}
                        </span>
                      </div>
                    </div>

                    {/* Quick Contact Buttons - Only show when unlocked */}
                    {lead.is_unlocked && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        <a
                          href={`tel:${lead.customer_phone}`}
                          onClick={() => logContact(lead.id, 'call')}
                          className="flex items-center justify-center gap-1 px-3 py-2 bg-[#1e8e3e] text-white rounded-full text-sm font-medium hover:bg-[#1a7c37] transition-colors"
                          data-testid={`call-btn-${lead.id}`}
                        >
                          <Phone className="w-4 h-4" />
                          Call
                        </a>
                        <a
                          href={`mailto:${lead.customer_email}?subject=Re: Your ${lead.service_category} Service Request`}
                          onClick={() => logContact(lead.id, 'email')}
                          className="flex items-center justify-center gap-1 px-3 py-2 bg-[#1a73e8] text-white rounded-full text-sm font-medium hover:bg-[#1557b0] transition-colors"
                          data-testid={`email-btn-${lead.id}`}
                        >
                          <Mail className="w-4 h-4" />
                          Email
                        </a>
                        <a
                          href={`https://wa.me/${lead.customer_phone.replace(/\D/g, '')}?text=Hi ${lead.customer_name}, I'm responding to your ${lead.service_category} service request on LocalPros.`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => logContact(lead.id, 'whatsapp')}
                          className="flex items-center justify-center gap-1 px-3 py-2 bg-[#25D366] text-white rounded-full text-sm font-medium hover:bg-[#20bd5a] transition-colors"
                          data-testid={`whatsapp-btn-${lead.id}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                          WhatsApp
                        </a>
                      </div>
                    )}

                    {/* Action Button */}
                    {!lead.is_unlocked && (
                      <Button
                        onClick={() => handleUnlockLead(lead.id)}
                        disabled={purchasingLeadId === lead.id}
                        className="w-full mt-4 bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
                        data-testid={`unlock-lead-${lead.id}`}
                      >
                        {purchasingLeadId === lead.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : subscription?.has_unlimited || subscription?.leads_remaining > 0 ? (
                          <>
                            <Unlock className="w-4 h-4 mr-2" />
                            Unlock Lead
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Unlock for ${lead.per_lead_price}
                          </>
                        )}
                      </Button>
                    )}

                    {lead.is_unlocked && (
                      <div className="mt-4 p-3 bg-[#e6f4ea] rounded-xl text-center">
                        <CheckCircle className="w-5 h-5 text-[#1e8e3e] inline mr-2" />
                        <span className="text-sm font-medium text-[#1e8e3e]">Contact info unlocked</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Modal */}
      <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
        <DialogContent className="max-w-3xl" data-testid="pricing-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-medium text-center">
              Choose Your Plan
            </DialogTitle>
            <p className="text-[#5f6368] text-center">
              Select a plan that works best for you
            </p>
          </DialogHeader>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {pricing.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 p-6 transition-all ${
                  plan.id === "monthly_pro"
                    ? "border-[#1a73e8] bg-[#e8f0fe]/30"
                    : "border-[#dadce0] hover:border-[#1a73e8]/50"
                }`}
              >
                {plan.id === "monthly_pro" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1a73e8] text-white text-xs font-medium px-3 py-1 rounded-full">
                    Best Value
                  </div>
                )}

                <div className="text-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#f8f9fa] flex items-center justify-center mx-auto mb-3">
                    {plan.id === "per_lead" ? (
                      <CreditCard className="w-6 h-6 text-[#5f6368]" />
                    ) : plan.id === "monthly_basic" ? (
                      <Star className="w-6 h-6 text-[#f9ab00]" />
                    ) : (
                      <Crown className="w-6 h-6 text-[#1a73e8]" />
                    )}
                  </div>
                  <h3 className="font-medium text-[#202124]">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-[#202124]">${plan.price}</span>
                    <span className="text-[#5f6368]">
                      {plan.id === "per_lead" ? "/lead" : "/month"}
                    </span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[#5f6368]">
                      <Check className="w-4 h-4 text-[#1e8e3e]" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handlePurchasePlan(plan.id)}
                  disabled={processingPlan === plan.id}
                  className={`w-full rounded-full ${
                    plan.id === "monthly_pro"
                      ? "bg-[#1a73e8] hover:bg-[#1557b0] text-white"
                      : "bg-white border border-[#dadce0] text-[#202124] hover:bg-[#f8f9fa]"
                  }`}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {processingPlan === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : plan.id === "per_lead" ? (
                    "Buy This Lead"
                  ) : (
                    "Subscribe"
                  )}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default LeadsPage;
